var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React, { useMemo, useRef, useState, useCallback } from 'react';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { translate } from '@utils/i18n';
import { dispatchWidgetCommand } from '@features/AIChatWidget/services/widgetCommandBus';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@ui/molecules/Modal';
import { Brushaway, BrushawayHeader, BrushawayBody, BrushawayFooter } from '@ui/molecules/Brushaway';
import { MarkdownText } from '@ui/molecules/MarkdownText';
import { Button } from '@ui/atoms/Button';
import { Text } from '@ui/atoms/Text';
import { Loader } from '@ui';
import { TabView } from '@ui/molecules/TabView';
import { WorkspaceHeader } from '@ui/molecules/Header/WorkspaceHeader';
import { Table, TableSize } from '@ui/molecules/Table';
import { Checkbox } from '@ui/molecules/inputs/Checkbox';
import { SelectInput } from '@ui/molecules/inputs/form/SelectInput';
import { ExclusionMultiSelect } from '@features/Settings/AiInspectionManager/components/UserSettings/exclusions/ExclusionMultiSelect';
import { listDocuments, getDocumentContent, getDocChildRuleStats, updateDocument, deleteDocument, listRules, createRule, updateRule, deleteRule, searchBuildings, searchAccounts, searchTechnicians, searchTeams } from './services/TkbService';
import { isDocumentIndexed, isDocumentIndexing } from './documentIndexStatus';
import { RULE_ENFORCEMENT_BLOCK, RULE_ENFORCEMENT_GUIDANCE } from './types';
import AppliesTo from './components/AppliesTo';
import RuleEnforcementChoice from './components/RuleEnforcementChoice';
import PreviewPanel from './components/PreviewPanel';
import VersionDrawer from './components/VersionDrawer';
import AssistantPill from './components/AssistantPill';
import AddDocumentModal from './components/AddDocumentModal';
import TkbFilterBar from './components/TkbFilterBar';
import './styles.scss';
// ── i18n ──────────────────────────────────────────────────────────────────────
const namespace = 'features.settings.tenant_knowledge_base';
const t = (key, params = {}) => translate(key, Object.assign({ namespace }, params));
// ── Query keys ────────────────────────────────────────────────────────────────
const TKB_QUERY_KEYS = {
    documents: ['tkb', 'documents'],
    rules: ['tkb', 'rules']
};
function IconButton({ icon, title, ariaLabel, onClick, disabled }) {
    return (React.createElement("button", { type: "button", title: title, "aria-label": ariaLabel, className: "qmb-control--icon", disabled: disabled, onClick: onClick },
        React.createElement("i", { className: icon, "aria-hidden": "true" })));
}
IconButton.defaultProps = { disabled: false };
function DocsTab() {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const qc = useQueryClient();
    const [contentMarkdown, setContentMarkdown] = useState(null);
    const [contentTitle, setContentTitle] = useState('');
    const [isContentModalOpen, setContentModalOpen] = useState(false);
    const [isAddModalOpen, setAddModalOpen] = useState(false);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const [toggleError, setToggleError] = useState(null);
    const [showInactive, setShowInactive] = useState(false);
    const [searchText, setSearchText] = useState('');
    // Documents filters — rendered as native-select pills matching the Rules tab.
    const [audienceFilter, setAudienceFilter] = useState('all');
    const [uploadedByFilter, setUploadedByFilter] = useState('all');
    const [uploadDateFilter, setUploadDateFilter] = useState('all');
    const [deactivateCascadeModal, setDeactivateCascadeModal] = useState({
        isOpen: false,
        docId: null,
        stats: null
    });
    const [reactivateCascadeModal, setReactivateCascadeModal] = useState({
        isOpen: false,
        docId: null,
        inactiveCount: 0
    });
    const contentRequestRef = useRef(null);
    const { data, isLoading, isError, error } = useQuery({
        queryKey: TKB_QUERY_KEYS.documents,
        queryFn: listDocuments
    });
    const toggleActiveMutation = useMutation({
        mutationFn: ({ id, active, cascade_rules }) => updateDocument(id, Object.assign({ active }, (cascade_rules ? { cascade_rules } : {}))),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: TKB_QUERY_KEYS.documents });
            setDeactivateCascadeModal({ isOpen: false, docId: null, stats: null });
            setReactivateCascadeModal({ isOpen: false, docId: null, inactiveCount: 0 });
        }
    });
    const handleToggleActive = (doc) => __awaiter(this, void 0, void 0, function* () {
        const nextActive = !doc.active;
        if (nextActive) {
            try {
                const stats = yield getDocChildRuleStats(doc.id);
                const inactiveCount = stats.total - stats.active;
                if (inactiveCount === 0) {
                    toggleActiveMutation.mutate({ id: doc.id, active: true });
                }
                else {
                    setReactivateCascadeModal({ isOpen: true, docId: doc.id, inactiveCount });
                }
            }
            catch (_a) {
                toggleActiveMutation.mutate({ id: doc.id, active: true });
            }
            return;
        }
        try {
            const stats = yield getDocChildRuleStats(doc.id);
            if (stats.active === 0) {
                toggleActiveMutation.mutate({ id: doc.id, active: false });
            }
            else if (stats.edited === 0) {
                toggleActiveMutation.mutate({ id: doc.id, active: false, cascade_rules: 'all' });
            }
            else {
                setDeactivateCascadeModal({ isOpen: true, docId: doc.id, stats });
            }
        }
        catch (_b) {
            setToggleError(t('documents.toggle_error'));
        }
    });
    const handleCascadeConfirm = (cascade_rules) => {
        if (deactivateCascadeModal.docId !== null) {
            toggleActiveMutation.mutate({ id: deactivateCascadeModal.docId, active: false, cascade_rules });
        }
    };
    const handleCascadeCancel = () => {
        setDeactivateCascadeModal({ isOpen: false, docId: null, stats: null });
    };
    const handleReactivateConfirm = (cascade_rules) => {
        if (reactivateCascadeModal.docId !== null) {
            toggleActiveMutation.mutate({ id: reactivateCascadeModal.docId, active: true, cascade_rules });
        }
    };
    const handleReactivateCancel = () => {
        setReactivateCascadeModal({ isOpen: false, docId: null, inactiveCount: 0 });
    };
    const deleteMutation = useMutation({
        mutationFn: (id) => deleteDocument(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: TKB_QUERY_KEYS.documents });
            qc.invalidateQueries({ queryKey: TKB_QUERY_KEYS.rules });
            setDeleteConfirmId(null);
            setDeleteError(null);
        },
        onError: () => setDeleteError(t('documents.delete_modal.error'))
    });
    const handleViewContent = (doc) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        const token = Symbol('content-request');
        contentRequestRef.current = token;
        setContentMarkdown(null);
        setContentTitle(doc.title);
        setContentModalOpen(true);
        try {
            const result = yield getDocumentContent(doc.id);
            if (contentRequestRef.current === token) {
                setContentMarkdown((_a = result.content) !== null && _a !== void 0 ? _a : '');
            }
        }
        catch (_b) {
            if (contentRequestRef.current === token) {
                setContentMarkdown(t('documents.content_load_failure'));
            }
        }
    });
    const formatAudience = (audience) => {
        const known = ['office', 'field', 'both'];
        return known.includes(audience) ? t(`documents.audience.${audience}`) : audience;
    };
    const allDocuments = (_a = data === null || data === void 0 ? void 0 : data.documents) !== null && _a !== void 0 ? _a : [];
    const hiddenCount = allDocuments.length - allDocuments.filter((d) => d.active).length;
    // Distinct uploader names for the "Uploaded By" filter (skip blanks).
    const uploaderOptions = useMemo(() => Array.from(new Set(allDocuments.map((d) => d.uploaded_by).filter((name) => !!name))).sort(), [allDocuments]);
    // Upper bound (ms) for the recency filter; a doc passes if uploaded within N days.
    const uploadDateCutoff = useMemo(() => {
        if (uploadDateFilter === 'all')
            return null;
        const days = Number(uploadDateFilter);
        return Date.now() - days * 24 * 60 * 60 * 1000;
    }, [uploadDateFilter]);
    const documents = allDocuments
        .filter((d) => (showInactive ? true : d.active))
        .filter((d) => (audienceFilter === 'all' ? true : d.audience === audienceFilter))
        .filter((d) => (uploadedByFilter === 'all' ? true : d.uploaded_by === uploadedByFilter))
        .filter((d) => (uploadDateCutoff === null ? true : new Date(d.uploaded_at).getTime() >= uploadDateCutoff))
        .filter((d) => (searchText.trim() === '' ? true : d.title.toLowerCase().includes(searchText.toLowerCase())));
    const columns = [
        {
            dataKey: 'title',
            label: t('documents.headers.title'),
            columnType: 'primary',
            width: '30%',
            // Indexing state sits on the title, next to the thing it qualifies.
            // Without it an unsearchable document is indistinguishable from a
            // working one: a failed ingest still lists, still shows an upload date,
            // still reports its chunks. The only tenant-visible symptom used to be
            // the assistant quietly answering from general knowledge instead.
            renderer: ({ item }) => (React.createElement(React.Fragment, null,
                item.title,
                !isDocumentIndexed(item) && (React.createElement("span", { className: `tkb-status-flag tkb-status-flag--${isDocumentIndexing(item) ? 'zero-match' : 'conflict'}`, "data-testid": `doc-index-status-${item.id}`, title: t('documents.index_status.tooltip') },
                    React.createElement("i", { className: `fa-light ${isDocumentIndexing(item) ? 'fa-clock' : 'fa-triangle-exclamation'}`, "aria-hidden": "true" }),
                    ' ',
                    isDocumentIndexing(item)
                        ? t('documents.index_status.processing')
                        : t('documents.index_status.failed')))))
        },
        {
            dataKey: 'uploaded_at',
            label: t('documents.headers.uploaded'),
            width: '18%',
            renderer: ({ item }) => React.createElement(React.Fragment, null, new Date(item.uploaded_at).toLocaleDateString())
        },
        { dataKey: 'uploaded_by', label: t('documents.headers.uploaded_by'), width: '18%' },
        {
            dataKey: 'audience',
            label: t('documents.headers.audience'),
            renderer: ({ item }) => React.createElement(React.Fragment, null, formatAudience(item.audience))
        },
        {
            dataKey: 'active',
            label: t('documents.headers.active'),
            width: '64px',
            renderer: ({ item }) => (React.createElement(Checkbox, { id: `tkb-doc-active-${item.id}`, checked: item.active, onChange: () => handleToggleActive(item), disabled: toggleActiveMutation.isLoading, "aria-label": t('documents.actions.toggle_active_aria', { title: item.title }) }))
        },
        {
            dataKey: 'actions',
            label: t('documents.headers.actions'),
            width: '96px',
            align: 'right',
            columnType: 'last',
            renderer: ({ item }) => (React.createElement("div", { className: "tkb-row-actions" },
                React.createElement(IconButton, { icon: "fa-light fa-eye", title: t('documents.actions.view'), ariaLabel: t('documents.actions.view_aria', { title: item.title }), onClick: () => handleViewContent(item) }),
                React.createElement(IconButton, { icon: "fa-light fa-trash-can", title: t('documents.actions.delete'), ariaLabel: t('documents.actions.delete_aria', { title: item.title }), onClick: () => setDeleteConfirmId(item.id) })))
        }
    ];
    if (isLoading) {
        return (React.createElement("div", { className: "loading-center" },
            React.createElement(Loader, { size: "64" })));
    }
    if (isError) {
        return (React.createElement(Text, { style: { color: 'var(--color-danger, #c0392b)' } }, (error === null || error === void 0 ? void 0 : error.message) || t('documents.load_failure')));
    }
    return (React.createElement("div", null,
        toggleError && React.createElement(Text, { style: { color: 'var(--color-danger, #c0392b)', marginBottom: '8px' } }, toggleError),
        React.createElement(TkbFilterBar, { addLabel: t('documents.add_button'), onAdd: () => setAddModalOpen(true), searchId: "tkb-docs-search", searchValue: searchText, searchPlaceholder: t('documents.search_placeholder'), onSearchChange: setSearchText, filterLabel: t('filters.label'), showInactiveId: "tkb-show-inactive-docs", showInactiveLabel: hiddenCount > 0 ? t('filters.show_inactive_with_count', { count: hiddenCount }) : t('filters.show_inactive'), showInactive: showInactive, onShowInactiveChange: setShowInactive },
            React.createElement("div", { className: `filters__option--native${uploadDateFilter !== 'all' ? ' --active' : ''}` },
                React.createElement("select", { id: "tkb-docs-upload-date-filter", value: uploadDateFilter, onChange: (e) => setUploadDateFilter(e.target.value) },
                    React.createElement("option", { value: "all" }, t('filters.all')),
                    React.createElement("option", { value: "30" }, t('filters.upload_date_30')),
                    React.createElement("option", { value: "90" }, t('filters.upload_date_90')),
                    React.createElement("option", { value: "365" }, t('filters.upload_date_365'))),
                React.createElement("label", { htmlFor: "tkb-docs-upload-date-filter", className: "qmb-label" }, t('filters.upload_date'))),
            React.createElement("div", { className: `filters__option--native${uploadedByFilter !== 'all' ? ' --active' : ''}` },
                React.createElement("select", { id: "tkb-docs-uploaded-by-filter", value: uploadedByFilter, onChange: (e) => setUploadedByFilter(e.target.value) },
                    React.createElement("option", { value: "all" }, t('filters.all')),
                    uploaderOptions.map((name) => (React.createElement("option", { key: name, value: name }, name)))),
                React.createElement("label", { htmlFor: "tkb-docs-uploaded-by-filter", className: "qmb-label" }, t('filters.uploaded_by'))),
            React.createElement("div", { className: `filters__option--native${audienceFilter !== 'all' ? ' --active' : ''}` },
                React.createElement("select", { id: "tkb-docs-audience-filter", value: audienceFilter, onChange: (e) => setAudienceFilter(e.target.value) },
                    React.createElement("option", { value: "all" }, t('filters.all')),
                    React.createElement("option", { value: "office" }, t('documents.audience.office')),
                    React.createElement("option", { value: "field" }, t('documents.audience.field')),
                    React.createElement("option", { value: "both" }, t('documents.audience.both'))),
                React.createElement("label", { htmlFor: "tkb-docs-audience-filter", className: "qmb-label" }, t('filters.audience')))),
        documents.length === 0 ? (React.createElement("p", { className: "no-data-message", style: { textAlign: 'center', padding: '16px' } }, allDocuments.length === 0 ? t('documents.empty') : t('documents.empty_active'))) : (React.createElement(Table, { variant: "detail", size: TableSize.Full, columns: columns, data: documents, getItemId: (item) => item.id })),
        React.createElement(AddDocumentModal, { isOpen: isAddModalOpen, onClose: () => setAddModalOpen(false), onUpload: (files) => {
                dispatchWidgetCommand({
                    type: 'ingest-documents',
                    files,
                    message: t('documents.add_modal.ingest_message')
                });
                setAddModalOpen(false);
            }, t: t }),
        React.createElement(Modal, { isOpen: isContentModalOpen, onClose: () => setContentModalOpen(false), ariaLabel: contentTitle || t('documents.content_modal.title') },
            React.createElement(ModalHeader, { title: contentTitle || t('documents.content_modal.title') }),
            React.createElement(ModalBody, null, contentMarkdown === null ? React.createElement(Loader, { size: "32" }) : React.createElement(MarkdownText, { content: contentMarkdown })),
            React.createElement(ModalFooter, null,
                React.createElement(Button, { variant: "secondary", onClick: () => setContentModalOpen(false) }, t('documents.content_modal.close')))),
        React.createElement(Modal, { isOpen: deleteConfirmId !== null, ariaLabel: t('documents.delete_modal.title'), onClose: () => {
                setDeleteConfirmId(null);
                setDeleteError(null);
            } },
            React.createElement(ModalHeader, { title: t('documents.delete_modal.title') }),
            React.createElement(ModalBody, null,
                React.createElement(Text, { as: "p" }, t('documents.delete_modal.body')),
                deleteError && (React.createElement(Text, { as: "p", style: { color: 'var(--color-danger, #c0392b)', marginTop: '8px' } }, deleteError))),
            React.createElement(ModalFooter, null,
                React.createElement(Button, { variant: "secondary", onClick: () => {
                        setDeleteConfirmId(null);
                        setDeleteError(null);
                    }, style: { marginRight: '8px' } }, t('documents.delete_modal.cancel')),
                React.createElement(Button, { variant: "danger", disabled: deleteMutation.isLoading, onClick: () => {
                        if (deleteConfirmId !== null)
                            deleteMutation.mutate(deleteConfirmId);
                    } }, deleteMutation.isLoading ? t('documents.delete_modal.deleting') : t('documents.delete_modal.confirm')))),
        React.createElement(Modal, { isOpen: deactivateCascadeModal.isOpen, onClose: handleCascadeCancel, ariaLabel: t('deactivate_modal.title') },
            React.createElement(ModalHeader, { title: t('deactivate_modal.title') }),
            React.createElement(ModalBody, null,
                React.createElement("p", { style: { margin: 0 } },
                    t(((_c = (_b = deactivateCascadeModal.stats) === null || _b === void 0 ? void 0 : _b.total) !== null && _c !== void 0 ? _c : 0) === 1
                        ? 'deactivate_modal.body_total_one'
                        : 'deactivate_modal.body_total_other', { total: (_e = (_d = deactivateCascadeModal.stats) === null || _d === void 0 ? void 0 : _d.total) !== null && _e !== void 0 ? _e : 0 }),
                    ' ',
                    t(((_g = (_f = deactivateCascadeModal.stats) === null || _f === void 0 ? void 0 : _f.edited) !== null && _g !== void 0 ? _g : 0) === 1
                        ? 'deactivate_modal.body_edited_one'
                        : 'deactivate_modal.body_edited_other', { edited: (_j = (_h = deactivateCascadeModal.stats) === null || _h === void 0 ? void 0 : _h.edited) !== null && _j !== void 0 ? _j : 0 })),
                React.createElement("p", { style: { marginTop: '12px', marginBottom: 0, color: 'var(--color-muted, #666)', fontSize: '0.9em' } }, t('deactivate_modal.reactivate_hint'))),
            React.createElement(ModalFooter, null,
                React.createElement(Button, { variant: "secondary", onClick: handleCascadeCancel, style: { marginRight: '8px' } }, t('deactivate_modal.cancel')),
                React.createElement(Button, { variant: "secondary", onClick: () => handleCascadeConfirm('preserve_edited'), style: { marginRight: '8px' } }, t('deactivate_modal.keep_edited')),
                React.createElement(Button, { variant: "danger", onClick: () => handleCascadeConfirm('all') }, t('deactivate_modal.deactivate_all')))),
        React.createElement(Modal, { isOpen: reactivateCascadeModal.isOpen, onClose: handleReactivateCancel, ariaLabel: t('reactivate_modal.title') },
            React.createElement(ModalHeader, { title: t('reactivate_modal.title') }),
            React.createElement(ModalBody, null,
                React.createElement("p", { style: { margin: 0 } }, t(reactivateCascadeModal.inactiveCount === 1 ? 'reactivate_modal.body_one' : 'reactivate_modal.body_other', { inactive: reactivateCascadeModal.inactiveCount })),
                React.createElement("p", { style: { marginTop: '12px', marginBottom: 0 } }, t('reactivate_modal.prompt'))),
            React.createElement(ModalFooter, null,
                React.createElement(Button, { variant: "secondary", onClick: handleReactivateCancel, style: { marginRight: '8px' } }, t('reactivate_modal.cancel')),
                React.createElement(Button, { variant: "secondary", onClick: () => handleReactivateConfirm('none'), style: { marginRight: '8px' } }, t('reactivate_modal.leave_inactive')),
                React.createElement(Button, { variant: "primary", onClick: () => handleReactivateConfirm('reactivate_all') }, t('reactivate_modal.reactivate_all'))))));
}
// Pull the safety-gate reject reason out of an axios 422 envelope
// ({ verdict: 'reject', error }); fall back to the generic save-failed copy.
function rejectReason(err) {
    var _a;
    const data = (_a = err.response) === null || _a === void 0 ? void 0 : _a.data;
    return (data === null || data === void 0 ? void 0 : data.error) || t('rules.gate.save_failed');
}
// Pull the conflict list out of a 409 envelope ({ error, conflicts: [...] }).
// Returns null when the error is not a conflict block.
function conflictsFromError(err) {
    var _a;
    const resp = err.response;
    if ((resp === null || resp === void 0 ? void 0 : resp.status) !== 409)
        return null;
    const list = (_a = resp.data) === null || _a === void 0 ? void 0 : _a.conflicts;
    return Array.isArray(list) && list.length > 0 ? list : null;
}
const EMPTY_RULE_FORM = {
    scope: '',
    enforcement: RULE_ENFORCEMENT_GUIDANCE,
    directive: '',
    scopeLevel: 'tenant',
    targetBuildings: [],
    targetAccounts: [],
    buildingTag: '',
    accountTag: '',
    targetTechnicians: [],
    targetTeams: [],
    appliesTo: ['ipa'],
    relevantChecks: [],
    actorIaDefaultResolved: false
};
/** Surfaces the tenant may author — strips IA/IPA the entitlement flags disallow. */
function clampEntitledAppliesTo(surfaces, capabilities) {
    const iaEntitled = capabilities.ai_rules_knowledge && capabilities.ia_present;
    const ipaEntitled = capabilities.ipa_present !== false;
    const entitled = surfaces.filter((surface) => {
        if (surface === 'ia')
            return iaEntitled;
        if (surface === 'ipa')
            return ipaEntitled;
        return false;
    });
    if (entitled.length > 0)
        return entitled;
    if (ipaEntitled)
        return ['ipa'];
    if (iaEntitled)
        return ['ia'];
    return [];
}
function hasEntitledRuleSurface(capabilities) {
    return clampEntitledAppliesTo(['ipa', 'ia'], capabilities).length > 0;
}
/**
 * One-shot default: the first actor target in an editing session ticks IA. Tracked by a
 * flag rather than the current target counts, so clearing every target and picking a new
 * one does not re-apply a default the author already overrode.
 */
function actorSurfaceDefault(prev, nextTechnicians, nextTeams, iaEntitled) {
    const unchanged = { appliesTo: prev.appliesTo, actorIaDefaultResolved: prev.actorIaDefaultResolved };
    if (prev.actorIaDefaultResolved || !iaEntitled || prev.appliesTo.includes('ia'))
        return unchanged;
    if (nextTechnicians.length === 0 && nextTeams.length === 0)
        return unchanged;
    return { appliesTo: [...prev.appliesTo, 'ia'], actorIaDefaultResolved: true };
}
function rememberSearchOptions(current, incoming) {
    const byValue = new Map(current.map((option) => [option.value, option]));
    incoming.forEach((option) => byValue.set(option.value, option));
    return Array.from(byValue.values());
}
function resolveSelectedTargets(values, selectedTargets, availableOptions) {
    return values.map((value) => {
        var _a;
        const id = Number(value);
        const selected = selectedTargets.find((target) => target.id === id);
        const available = availableOptions.find((option) => option.value === id);
        return selected !== null && selected !== void 0 ? selected : { id, name: (_a = available === null || available === void 0 ? void 0 : available.label) !== null && _a !== void 0 ? _a : value };
    });
}
function RulesTab({ scopeLabels, prohibitableScopeLabels, capabilities, buildingTags, accountTags, issueTypes, designatedRuleContextChecks }) {
    var _a, _b;
    const qc = useQueryClient();
    const [modalState, setModalState] = useState({ isOpen: false, editingRule: null });
    const [form, setForm] = useState(EMPTY_RULE_FORM);
    const [formError, setFormError] = useState('');
    // Hard-block saves name the action and wait for an explicit confirm.
    const [blockConfirmOpen, setBlockConfirmOpen] = useState(false);
    // A reword "Use suggestion" is also a save. Hold its overrides until the
    // hard-block confirm, so that path cannot skip the named confirmation.
    const pendingSaveRef = useRef(undefined);
    // Manual-entry safety gate: a `reword` verdict carries a salvaged proposal the
    // admin can accept in one click (nl-extraction-output-v1). Nothing persisted yet.
    const [reword, setReword] = useState(null);
    // Hard conflict block (409): the candidate semantically opposes an existing
    // same-scope rule. The admin edits/deactivates the opposing rule, or clicks
    // "Save anyway" to re-submit with conflict_acknowledged=true.
    const [conflict, setConflict] = useState(null);
    // Surfaces a reactivation block (409) from the rules-list active toggle, where
    // there's no open drawer to show the conflict panel.
    const [toggleError, setToggleError] = useState(null);
    const [deleteConfirmRule, setDeleteConfirmRule] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const [versionsRule, setVersionsRule] = useState(null);
    const [showInactive, setShowInactive] = useState(false);
    const [scopeLevelFilter, setScopeLevelFilter] = useState('all');
    const [surfaceFilter, setSurfaceFilter] = useState('all');
    const [searchText, setSearchText] = useState('');
    const [buildingOptions, setBuildingOptions] = useState([]);
    const [accountOptions, setAccountOptions] = useState([]);
    const [technicianOptions, setTechnicianOptions] = useState([]);
    const [teamOptions, setTeamOptions] = useState([]);
    const iaEntitled = capabilities.ai_rules_knowledge && capabilities.ia_present;
    const ipaEntitled = capabilities.ipa_present !== false;
    // Surface picker only when IA is entitled (dual-product); IPA-only tenants
    // default to applies_to=[ipa] with no extra control.
    const showSurfaceChip = iaEntitled;
    const canAuthorRules = hasEntitledRuleSurface(capabilities);
    const designatedCheckOptions = issueTypes.filter((it) => designatedRuleContextChecks.includes(it.value));
    const { data, isLoading, isError, error } = useQuery({ queryKey: TKB_QUERY_KEYS.rules, queryFn: listRules });
    // Outcome handler shared by create + update: `saved` closes the drawer;
    // `reword` keeps it open and surfaces the proposal (nothing persisted).
    const handleSaveResult = (result) => {
        if (result.kind === 'reword') {
            setReword(result.proposal);
            setFormError('');
            return;
        }
        setReword(null);
        qc.invalidateQueries({ queryKey: TKB_QUERY_KEYS.rules });
        closeModal();
    };
    // A 409 conflict surfaces the conflict panel (with a one-click acknowledge);
    // any other error falls back to the inline reject reason.
    const handleSaveError = (err) => {
        const detected = conflictsFromError(err);
        if (detected) {
            setConflict(detected);
            setFormError('');
            return;
        }
        setConflict(null);
        setFormError(rejectReason(err));
    };
    const createMutation = useMutation({
        mutationFn: (payload) => createRule(payload),
        onSuccess: handleSaveResult,
        onError: handleSaveError
    });
    const updateMutation = useMutation({
        mutationFn: ({ id, payload }) => updateRule(id, payload),
        onSuccess: handleSaveResult,
        onError: handleSaveError
    });
    const deleteMutation = useMutation({
        mutationFn: (id) => deleteRule(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: TKB_QUERY_KEYS.rules });
            setDeleteConfirmRule(null);
            setDeleteError(null);
        },
        onError: () => setDeleteError(t('rules.delete_modal.error'))
    });
    const toggleActiveMutation = useMutation({
        mutationFn: ({ id, active }) => updateRule(id, { active }),
        onSuccess: () => {
            setToggleError(null);
            qc.invalidateQueries({ queryKey: TKB_QUERY_KEYS.rules });
        },
        // Reactivation can be blocked by a 409 conflict with a now-active opposing
        // rule; surface it inline (the conflict panel lives in the drawer, which is
        // closed during a list-row toggle).
        onError: (err) => {
            const detected = conflictsFromError(err);
            setToggleError(detected
                ? detected
                    .map((c) => c.reason)
                    .filter(Boolean)
                    .join(' ') || rejectReason(err)
                : rejectReason(err));
        }
    });
    const loadBuildingOptions = useCallback((query) => __awaiter(this, void 0, void 0, function* () {
        try {
            const results = yield searchBuildings(query);
            setBuildingOptions((current) => rememberSearchOptions(current, results));
            return results.map((option) => ({ value: String(option.value), label: option.label }));
        }
        catch (_a) {
            return [];
        }
    }), []);
    const loadAccountOptions = useCallback((query) => __awaiter(this, void 0, void 0, function* () {
        try {
            const results = yield searchAccounts(query);
            setAccountOptions((current) => rememberSearchOptions(current, results));
            return results.map((option) => ({ value: String(option.value), label: option.label }));
        }
        catch (_a) {
            return [];
        }
    }), []);
    const loadTechnicianOptions = useCallback((query) => __awaiter(this, void 0, void 0, function* () {
        try {
            const results = yield searchTechnicians(query);
            setTechnicianOptions((current) => rememberSearchOptions(current, results));
            return results.map((option) => ({ value: String(option.value), label: option.label }));
        }
        catch (_a) {
            return [];
        }
    }), []);
    const loadTeamOptions = useCallback((query) => __awaiter(this, void 0, void 0, function* () {
        try {
            const results = yield searchTeams(query);
            setTeamOptions((current) => rememberSearchOptions(current, results));
            return results.map((option) => ({ value: String(option.value), label: option.label }));
        }
        catch (_a) {
            return [];
        }
    }), []);
    const tagSelectOptions = useMemo(() => buildingTags.map((tag) => ({ value: tag, label: tag })), [buildingTags]);
    const accountTagSelectOptions = useMemo(() => accountTags.map((tag) => ({ value: tag, label: tag })), [accountTags]);
    // ── Drawer helpers ─────────────────────────────────────────────────────────
    const openAddModal = () => {
        setForm(Object.assign(Object.assign({}, EMPTY_RULE_FORM), { scope: 'auto', enforcement: RULE_ENFORCEMENT_GUIDANCE, appliesTo: clampEntitledAppliesTo(EMPTY_RULE_FORM.appliesTo, capabilities) }));
        setBuildingOptions([]);
        setAccountOptions([]);
        setTechnicianOptions([]);
        setTeamOptions([]);
        setFormError('');
        setReword(null);
        setConflict(null);
        pendingSaveRef.current = undefined;
        setBlockConfirmOpen(false);
        setModalState({ isOpen: true, editingRule: null });
    };
    const openEditModal = (rule) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        setForm({
            scope: rule.scope,
            enforcement: rule.rule_kind === RULE_ENFORCEMENT_BLOCK ? RULE_ENFORCEMENT_BLOCK : RULE_ENFORCEMENT_GUIDANCE,
            directive: rule.directive_human,
            scopeLevel: rule.scope_level,
            targetBuildings: (_a = rule.target_buildings) !== null && _a !== void 0 ? _a : [],
            targetAccounts: (_b = rule.target_accounts) !== null && _b !== void 0 ? _b : [],
            buildingTag: (_c = rule.building_tag) !== null && _c !== void 0 ? _c : '',
            accountTag: (_d = rule.account_tag) !== null && _d !== void 0 ? _d : '',
            targetTechnicians: (_e = rule.target_technicians) !== null && _e !== void 0 ? _e : [],
            targetTeams: (_f = rule.target_teams) !== null && _f !== void 0 ? _f : [],
            appliesTo: clampEntitledAppliesTo(rule.applies_to, capabilities),
            relevantChecks: rule.relevant_checks,
            actorIaDefaultResolved: ((_g = rule.target_technicians) !== null && _g !== void 0 ? _g : []).length > 0 || ((_h = rule.target_teams) !== null && _h !== void 0 ? _h : []).length > 0
        });
        setBuildingOptions([]);
        setAccountOptions([]);
        setTechnicianOptions([]);
        setTeamOptions([]);
        setFormError('');
        setReword(null);
        setConflict(null);
        pendingSaveRef.current = undefined;
        setBlockConfirmOpen(false);
        setModalState({ isOpen: true, editingRule: rule });
    };
    const closeModal = () => {
        setModalState({ isOpen: false, editingRule: null });
        setBuildingOptions([]);
        setAccountOptions([]);
        setTechnicianOptions([]);
        setTeamOptions([]);
        setFormError('');
        setReword(null);
        setConflict(null);
        pendingSaveRef.current = undefined;
        setBlockConfirmOpen(false);
    };
    // ── Scope validation ──────────────────────────────────────────────────────
    const missingScopeKeyField = () => {
        if (form.scopeLevel === 'accounts' && form.targetAccounts.length === 0) {
            return t('rules.form.account_label');
        }
        if (form.scopeLevel === 'buildings' && form.targetBuildings.length === 0) {
            return t('rules.form.building_label');
        }
        if (form.scopeLevel === 'building_tag' && !form.buildingTag.trim()) {
            return t('rules.form.tag_label');
        }
        if (form.scopeLevel === 'account_tag' && !form.accountTag.trim()) {
            return t('rules.form.account_tag_label');
        }
        return null;
    };
    // Single-axis payload (rule-substrate-v1 §A.1): only the active location axis
    // is sent; all location axes are explicitly cleared so an edit that narrows
    // the axis prunes the previous targets. Actor axes are always sent as-is —
    // they are orthogonal (D1) and not subject to the single-axis invariant.
    const scopePayloadFromForm = () => ({
        building_ids: form.scopeLevel === 'buildings' ? form.targetBuildings.map((b) => b.id) : [],
        account_ids: form.scopeLevel === 'accounts' ? form.targetAccounts.map((a) => a.id) : [],
        building_tag: form.scopeLevel === 'building_tag' && form.buildingTag.trim() ? form.buildingTag : null,
        account_tag: form.scopeLevel === 'account_tag' && form.accountTag.trim() ? form.accountTag : null,
        technician_ids: form.targetTechnicians.map((tech) => tech.id),
        team_ids: form.targetTeams.map((team) => team.id)
    });
    const formIsReady = (directive) => {
        if (!canAuthorRules) {
            setFormError(t('rules.form.no_entitled_surface'));
            return false;
        }
        if (form.enforcement === RULE_ENFORCEMENT_BLOCK &&
            !Object.prototype.hasOwnProperty.call(prohibitableScopeLabels, form.scope)) {
            setFormError(t('rules.form.enforcement_scope_required'));
            return false;
        }
        if (!form.scope.trim() || !directive.trim()) {
            setFormError(t('rules.form.validation_required'));
            return false;
        }
        const missingField = missingScopeKeyField();
        if (missingField) {
            setFormError(t('rules.form.scope_key_required', { field: missingField }));
            return false;
        }
        return true;
    };
    // rule_kind is sent only when the author chose it. Editing a governance or
    // context rule and leaving "take this into account" selected must not
    // rewrite that kind down to evaluative.
    const ruleKindFields = () => {
        if (!modalState.editingRule)
            return { rule_kind: form.enforcement };
        if (form.enforcement === RULE_ENFORCEMENT_BLOCK)
            return { rule_kind: RULE_ENFORCEMENT_BLOCK };
        if (modalState.editingRule.rule_kind === RULE_ENFORCEMENT_BLOCK) {
            return { rule_kind: RULE_ENFORCEMENT_GUIDANCE };
        }
        return {};
    };
    const handleEnforcementChange = (next) => {
        setBlockConfirmOpen(false);
        setForm((prev) => {
            if (next === RULE_ENFORCEMENT_BLOCK) {
                const keep = Object.prototype.hasOwnProperty.call(prohibitableScopeLabels, prev.scope) ? prev.scope : '';
                return Object.assign(Object.assign({}, prev), { enforcement: next, scope: keep });
            }
            const restored = modalState.editingRule ? modalState.editingRule.scope : 'auto';
            return Object.assign(Object.assign({}, prev), { enforcement: next, scope: restored });
        });
    };
    const refusedActionLabel = () => prohibitableScopeLabels[form.scope] || scopeLabels[form.scope] || form.scope;
    // Submit the form. Optional overrides let the reword "Use suggestion" flow
    // re-submit the gate's proposed directive + surface directly (one-click
    // accept) — the proposal then passes the gate as an `accept`.
    const submitRule = (overrides) => {
        var _a, _b;
        const directive = (_a = overrides === null || overrides === void 0 ? void 0 : overrides.directive) !== null && _a !== void 0 ? _a : form.directive;
        if (!formIsReady(directive))
            return;
        // Clear a stale conflict panel on every fresh attempt; re-set by onError if
        // the gate still reports one (and not acknowledged).
        setConflict(null);
        setBlockConfirmOpen(false);
        const scopeKeys = scopePayloadFromForm();
        const appliesTo = clampEntitledAppliesTo((_b = overrides === null || overrides === void 0 ? void 0 : overrides.appliesTo) !== null && _b !== void 0 ? _b : form.appliesTo, capabilities);
        const relevantChecks = appliesTo.includes('ia') ? form.relevantChecks : [];
        const ackFields = (overrides === null || overrides === void 0 ? void 0 : overrides.acknowledgeConflict) ? { conflict_acknowledged: true } : {};
        const kindFields = ruleKindFields();
        const blockScope = form.enforcement === RULE_ENFORCEMENT_BLOCK ? { scope: form.scope } : {};
        if (modalState.editingRule) {
            updateMutation.mutate({
                id: modalState.editingRule.id,
                payload: Object.assign(Object.assign(Object.assign(Object.assign({ directive_human: directive, applies_to: appliesTo, relevant_checks: relevantChecks }, scopeKeys), kindFields), blockScope), ackFields)
            });
        }
        else {
            createMutation.mutate(Object.assign(Object.assign(Object.assign({ scope: form.scope, directive_human: directive, applies_to: appliesTo, relevant_checks: relevantChecks }, scopeKeys), kindFields), ackFields));
        }
    };
    const handleSubmit = () => {
        pendingSaveRef.current = undefined;
        if (!formIsReady(form.directive))
            return;
        if (form.enforcement === RULE_ENFORCEMENT_BLOCK) {
            setBlockConfirmOpen(true);
            return;
        }
        submitRule();
    };
    // Apply the gate's reworded proposal (directive + corrected surface) to the
    // form, then re-submit it. The proposed pair is what gets persisted on accept.
    const applyReword = (proposal) => {
        var _a;
        const nextAppliesTo = clampEntitledAppliesTo((_a = proposal.proposed_applies_to) !== null && _a !== void 0 ? _a : form.appliesTo, capabilities);
        setForm((prev) => (Object.assign(Object.assign({}, prev), { directive: proposal.proposed_directive_human, appliesTo: nextAppliesTo })));
        setReword(null);
        const overrides = { directive: proposal.proposed_directive_human, appliesTo: nextAppliesTo };
        if (form.enforcement === RULE_ENFORCEMENT_BLOCK) {
            if (!formIsReady(overrides.directive))
                return;
            pendingSaveRef.current = overrides;
            setBlockConfirmOpen(true);
            return;
        }
        submitRule(overrides);
    };
    const toggleSurface = (surface) => {
        setForm((prev) => {
            const has = prev.appliesTo.includes(surface);
            const next = has ? prev.appliesTo.filter((s) => s !== surface) : [...prev.appliesTo, surface];
            return Object.assign(Object.assign({}, prev), { appliesTo: next.length === 0 ? [surface] : next, actorIaDefaultResolved: surface === 'ia' ? true : prev.actorIaDefaultResolved });
        });
    };
    const toggleRelevantCheck = (value) => {
        setForm((prev) => {
            const has = prev.relevantChecks.includes(value);
            return Object.assign(Object.assign({}, prev), { relevantChecks: has ? prev.relevantChecks.filter((v) => v !== value) : [...prev.relevantChecks, value] });
        });
    };
    const sourceLabel = (source) => {
        const known = ['extracted', 'manual', 'extracted_edited'];
        return known.includes(source) ? t(`rules.source.${source}`) : source;
    };
    /**
     * Builds the multiselect target label for the "Applies to" cell — the strong
     * primary line. Server-resolved {id, name} targets render as
     * "Electric Forest, Memorial +1" (first two names, then "+N" overflow). Tag
     * axes pass the raw tag (AppliesTo wraps it). Tenant has no target. No raw IDs.
     */
    const TARGET_PREVIEW_COUNT = 2;
    const targetsLabel = (targets) => {
        if (targets.length === 0)
            return null;
        const shown = targets.slice(0, TARGET_PREVIEW_COUNT).map((tgt) => tgt.name);
        const overflow = targets.length - shown.length;
        const names = shown.join(', ');
        return overflow > 0 ? t('rules.applies_to.target_overflow', { names, count: overflow }) : names;
    };
    const scopeTargetLabel = (rule) => {
        var _a, _b;
        switch (rule.scope_level) {
            case 'accounts':
                return targetsLabel(rule.target_accounts);
            case 'buildings':
                return targetsLabel(rule.target_buildings);
            case 'building_tag':
                return (_a = rule.building_tag) !== null && _a !== void 0 ? _a : null;
            case 'account_tag':
                return (_b = rule.account_tag) !== null && _b !== void 0 ? _b : null;
            default:
                return null;
        }
    };
    // ── Filtering ─────────────────────────────────────────────────────────────
    const allRules = (_a = data === null || data === void 0 ? void 0 : data.rules) !== null && _a !== void 0 ? _a : [];
    const rules = allRules
        .filter((r) => (showInactive ? true : r.active))
        .filter((r) => (scopeLevelFilter === 'all' ? true : r.scope_level === scopeLevelFilter))
        .filter((r) => (surfaceFilter === 'all' ? true : r.applies_to.includes(surfaceFilter)))
        .filter((r) => searchText.trim() === '' ? true : r.directive_human.toLowerCase().includes(searchText.toLowerCase()));
    const hiddenCount = allRules.length - allRules.filter((r) => r.active).length;
    const scopeOptions = Object.entries(scopeLabels);
    const prohibitableOptions = useMemo(() => Object.entries(prohibitableScopeLabels)
        .map(([key, label]) => ({ value: key, label }))
        .sort((left, right) => left.label.localeCompare(right.label)), [prohibitableScopeLabels]);
    const isSaving = createMutation.isLoading || updateMutation.isLoading;
    if (isLoading) {
        return (React.createElement("div", { className: "loading-center" },
            React.createElement(Loader, { size: "64" })));
    }
    if (isError) {
        return (React.createElement(Text, { style: { color: 'var(--color-danger, #c0392b)' } }, (error === null || error === void 0 ? void 0 : error.message) || t('rules.load_failure')));
    }
    // ── Rules detail table columns ──────────────────────────────────────────────
    const columns = [
        {
            dataKey: 'directive_human',
            label: t('rules.headers.rule'),
            columnType: 'primary',
            width: '34%',
            renderer: ({ item }) => {
                var _a;
                const hasConflict = ((_a = item.conflicts) !== null && _a !== void 0 ? _a : []).some((c) => !c.acknowledged);
                return (React.createElement(React.Fragment, null,
                    item.directive_human,
                    item.rule_kind === RULE_ENFORCEMENT_BLOCK && (React.createElement("span", { className: "tkb-status-flag", "data-testid": `enforcement-badge-${item.id}` }, t('rules.form.enforcement_list_badge'))),
                    hasConflict && (React.createElement("span", { className: "tkb-status-flag tkb-status-flag--conflict", "data-testid": `conflict-badge-${item.id}`, title: t('rules.conflict_tooltip') },
                        React.createElement("i", { className: "fa-light fa-triangle-exclamation", "aria-hidden": "true" }),
                        " ",
                        t('rules.conflict')))));
            }
        },
        {
            dataKey: 'applies_to_cell',
            label: t('rules.headers.applies_to'),
            width: '20%',
            renderer: ({ item }) => {
                var _a, _b;
                const techs = (_a = item.target_technicians) !== null && _a !== void 0 ? _a : [];
                const teams = (_b = item.target_teams) !== null && _b !== void 0 ? _b : [];
                const actorLabel = targetsLabel([...techs, ...teams]);
                const actorKind = techs.length > 0 && teams.length > 0
                    ? t('rules.applies_to.kind_actor_mixed')
                    : techs.length > 0
                        ? t('rules.applies_to.kind_technician')
                        : t('rules.applies_to.kind_team');
                return (React.createElement(React.Fragment, null,
                    React.createElement(AppliesTo, { scopeLevel: item.scope_level, targetLabel: scopeTargetLabel(item), matchCount: item.scope_match_count, zeroMatch: item.zero_match, onChangeScopeRequest: item.zero_match ? () => openEditModal(item) : undefined, t: t }),
                    actorLabel && (React.createElement("span", { className: "tkb-applies tkb-applies--actor", "data-testid": "applies-actor" },
                        React.createElement("span", { className: "tkb-applies__target" }, actorLabel),
                        React.createElement("span", { className: "tkb-applies__meta" },
                            React.createElement("i", { className: "fa-light fa-user", "aria-hidden": "true" }),
                            actorKind)))));
            }
        }
    ];
    if (showSurfaceChip) {
        columns.push({
            dataKey: 'assistant',
            label: t('rules.headers.assistant'),
            width: '96px',
            renderer: ({ item }) => React.createElement(AssistantPill, { surfaces: item.applies_to, t: t })
        });
    }
    columns.push({
        dataKey: 'source',
        label: t('rules.headers.source'),
        width: '16%',
        renderer: ({ item }) => {
            var _a;
            return (React.createElement("span", { className: "tkb-source" },
                React.createElement("span", { className: "tkb-source__label" }, sourceLabel(item.source)),
                item.confidence !== null && (React.createElement("span", { className: "tkb-source__confidence" }, t('rules.confidence_value', { pct: Math.round(((_a = item.confidence) !== null && _a !== void 0 ? _a : 0) * 100) }))),
                (item.created_by_name != null || item.created_by_user_id != null) && (React.createElement("span", { className: "tkb-source__author", "data-testid": `rule-author-${item.id}` }, item.created_by_name ? item.created_by_name : t('rules.author_user_id', { id: item.created_by_user_id })))));
        }
    }, {
        dataKey: 'version',
        label: t('rules.headers.version'),
        width: '80px',
        renderer: ({ item }) => (React.createElement("span", { className: "tkb-source__version", "data-testid": `rule-version-${item.id}` }, t('rules.version_label', { version: item.version })))
    }, {
        dataKey: 'active',
        label: t('rules.headers.active'),
        width: '64px',
        renderer: ({ item }) => (React.createElement(Checkbox, { id: `tkb-rule-active-${item.id}`, checked: item.active, onChange: () => toggleActiveMutation.mutate({ id: item.id, active: !item.active }), disabled: toggleActiveMutation.isLoading, "aria-label": t('rules.actions.toggle_active_aria', { id: item.id }) }))
    }, {
        dataKey: 'actions',
        label: t('rules.headers.actions'),
        width: '120px',
        align: 'right',
        columnType: 'last',
        renderer: ({ item }) => (React.createElement("div", { className: "tkb-row-actions" },
            React.createElement(IconButton, { icon: "fa-light fa-clock-rotate-left", title: t('rules.actions.versions'), ariaLabel: t('rules.actions.versions_aria', { id: item.id }), onClick: () => setVersionsRule(item) }),
            React.createElement(IconButton, { icon: "fa-light fa-pencil", title: t('rules.actions.edit'), ariaLabel: t('rules.actions.edit_aria', { id: item.id }), onClick: () => openEditModal(item) }),
            React.createElement(IconButton, { icon: "fa-light fa-trash-can", title: t('rules.actions.delete'), ariaLabel: t('rules.actions.delete_aria', { id: item.id }), onClick: () => setDeleteConfirmRule(item) })))
    });
    return (React.createElement("div", null,
        toggleError && (React.createElement("div", { "data-testid": "rule-toggle-error", role: "alert", style: {
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                marginBottom: '12px',
                padding: '12px',
                border: '1px solid var(--red-04, #d0454c)',
                borderRadius: '4px',
                background: 'var(--red-01, #fdf0f0)'
            } },
            React.createElement("i", { className: "fa-light fa-triangle-exclamation", "aria-hidden": "true", style: { marginTop: '2px' } }),
            React.createElement("div", { style: { flex: 1 } },
                React.createElement(Text, { variant: "small", as: "p", style: { fontWeight: 600 } }, t('rules.gate.conflict_title')),
                React.createElement(Text, { variant: "small", as: "p", style: { marginTop: '2px' } }, toggleError)),
            React.createElement("button", { type: "button", className: "qmb-control--icon", "aria-label": t('rules.gate.dismiss_suggestion'), onClick: () => setToggleError(null) },
                React.createElement("i", { className: "fa-light fa-xmark", "aria-hidden": "true" })))),
        React.createElement(TkbFilterBar, { addLabel: t('rules.add_button'), onAdd: openAddModal, searchId: "rules-search", searchValue: searchText, searchPlaceholder: t('rules.filters.search_placeholder'), onSearchChange: setSearchText, filterLabel: t('filters.label'), showInactiveId: "tkb-show-inactive-rules", showInactiveLabel: hiddenCount > 0 ? t('filters.show_inactive_with_count', { count: hiddenCount }) : t('filters.show_inactive'), showInactive: showInactive, onShowInactiveChange: setShowInactive },
            React.createElement("div", { className: "filters__option--native --active" },
                React.createElement("select", { id: "rules-scope-filter", value: scopeLevelFilter, onChange: (e) => setScopeLevelFilter(e.target.value) },
                    React.createElement("option", { value: "all" }, t('rules.filters.all')),
                    React.createElement("option", { value: "tenant" }, t('rules.scope_badge.tenant')),
                    React.createElement("option", { value: "accounts" }, t('rules.filters.account')),
                    React.createElement("option", { value: "account_tag" }, t('rules.filters.account_tag')),
                    React.createElement("option", { value: "buildings" }, t('rules.scope_badge.building')),
                    React.createElement("option", { value: "building_tag" }, t('rules.filters.tag'))),
                React.createElement("label", { htmlFor: "rules-scope-filter", className: "qmb-label" }, t('rules.filters.scope_level'))),
            showSurfaceChip && (React.createElement("div", { className: "filters__option--native --active" },
                React.createElement("select", { id: "rules-surface-filter", value: surfaceFilter, onChange: (e) => setSurfaceFilter(e.target.value) },
                    React.createElement("option", { value: "all" }, t('rules.filters.all')),
                    React.createElement("option", { value: "ia" }, t('rules.surface.ia')),
                    React.createElement("option", { value: "ipa" }, t('rules.surface.ipa'))),
                React.createElement("label", { htmlFor: "rules-surface-filter", className: "qmb-label" }, t('rules.headers.assistant'))))),
        rules.length === 0 ? (React.createElement("p", { className: "no-data-message", style: { textAlign: 'center', padding: '16px' } }, allRules.length === 0 ? t('rules.empty') : t('rules.empty_active'))) : (React.createElement(Table, { variant: "detail", columns: columns, data: rules, getItemId: (item) => item.id })),
        React.createElement(Brushaway, { controlledOpen: modalState.isOpen, onClose: closeModal },
            React.createElement(BrushawayHeader, { title: modalState.editingRule ? t('rules.form.edit_title') : t('rules.form.add_title') }),
            React.createElement(BrushawayBody, null,
                React.createElement("div", { className: "tkb-rule-form" },
                    formError && (React.createElement(Text, { style: { color: 'var(--color-danger, #c0392b)', marginBottom: '12px' } }, formError)),
                    conflict && (React.createElement("div", { "data-testid": "rule-conflict", role: "alert", style: {
                            marginBottom: '12px',
                            padding: '12px',
                            border: '1px solid var(--red-04, #d0454c)',
                            borderRadius: '4px',
                            background: 'var(--red-01, #fdf0f0)'
                        } },
                        React.createElement(Text, { variant: "small", as: "p", style: { fontWeight: 600 } }, t('rules.gate.conflict_title')),
                        React.createElement(Text, { variant: "small", as: "p", style: { marginTop: '4px' } }, t('rules.gate.conflict_body')),
                        React.createElement("ul", { style: { margin: '8px 0 0', paddingLeft: '18px' } }, conflict.map((c) => (React.createElement("li", { key: c.rule_id, "data-testid": "rule-conflict-item" },
                            c.conflicting_directive && (React.createElement(Text, { variant: "small", as: "span", style: { fontStyle: 'italic' } },
                                "\u201C",
                                c.conflicting_directive,
                                "\u201D \u2014",
                                ' ')),
                            React.createElement(Text, { variant: "small", as: "span" }, c.reason))))),
                        React.createElement("div", { style: { display: 'flex', gap: '8px', marginTop: '8px' } },
                            React.createElement(Button, { variant: "primary", disabled: isSaving, onClick: () => submitRule({ acknowledgeConflict: true }) }, t('rules.gate.conflict_save_anyway')),
                            React.createElement(Button, { variant: "secondary", onClick: () => setConflict(null) }, t('rules.gate.dismiss_suggestion'))))),
                    reword && (React.createElement("div", { "data-testid": "rule-reword", role: "status", style: {
                            marginBottom: '12px',
                            padding: '12px',
                            border: '1px solid var(--blue-04, #5b8def)',
                            borderRadius: '4px',
                            background: 'var(--blue-01, #f0f5ff)'
                        } },
                        React.createElement(Text, { variant: "small", as: "p", style: { fontWeight: 600 } }, t('rules.gate.reword_title')),
                        reword.reason && (React.createElement(Text, { variant: "small", as: "p", style: { marginTop: '4px' } }, reword.reason)),
                        React.createElement("blockquote", { "data-testid": "rule-reword-suggestion", style: { margin: '8px 0 0', paddingLeft: '8px', borderLeft: '3px solid var(--blue-04, #5b8def)' } }, reword.proposed_directive_human),
                        reword.proposed_applies_to && (React.createElement(Text, { variant: "x-small", as: "p", "data-testid": "rule-reword-surface", style: { marginTop: '4px' } }, t('rules.gate.reword_surface', {
                            surfaces: reword.proposed_applies_to.map((s) => t(`rules.surface.${s}`)).join(', ')
                        }))),
                        reword.unentitled_applies_to && (React.createElement(Text, { variant: "x-small", as: "p", "data-testid": "rule-reword-unentitled-surface", style: { marginTop: '4px' } }, t('rules.gate.reword_surface_unavailable', {
                            surfaces: reword.unentitled_applies_to.map((s) => t(`rules.surface.${s}`)).join(', ')
                        }))),
                        React.createElement("div", { style: { display: 'flex', gap: '8px', marginTop: '8px' } },
                            React.createElement(Button, { variant: "primary", disabled: isSaving || Boolean((_b = reword.unentitled_applies_to) === null || _b === void 0 ? void 0 : _b.length), onClick: () => applyReword(reword) }, t('rules.gate.use_suggestion')),
                            React.createElement(Button, { variant: "secondary", onClick: () => setReword(null) }, t('rules.gate.dismiss_suggestion'))))),
                    React.createElement(RuleEnforcementChoice, { value: form.enforcement, onChange: handleEnforcementChange, t: t }),
                    React.createElement("div", { className: "tkb-rule-form__field" },
                        React.createElement(SelectInput, { id: "tkb-rule-scope", className: "qmb-ui-select--labeled", label: form.enforcement === RULE_ENFORCEMENT_BLOCK
                                ? t('rules.form.enforcement_scope_label')
                                : t('rules.form.when_applies_label'), options: form.enforcement === RULE_ENFORCEMENT_BLOCK
                                ? [{ value: '', label: t('rules.form.enforcement_scope_placeholder') }, ...prohibitableOptions]
                                : [
                                    { value: 'auto', label: t('rules.form.scope_auto') },
                                    ...scopeOptions.map(([key, label]) => ({ value: key, label }))
                                ], value: form.scope, onChange: (val) => setForm((prev) => (Object.assign(Object.assign({}, prev), { scope: val }))), disabled: Boolean(modalState.editingRule) && form.enforcement !== RULE_ENFORCEMENT_BLOCK, showSearch: form.enforcement === RULE_ENFORCEMENT_BLOCK, searchPlaceholder: t('rules.form.enforcement_scope_placeholder') })),
                    React.createElement("div", { className: "tkb-rule-form__field" },
                        React.createElement(SelectInput, { id: "tkb-rule-scope-level", className: "qmb-ui-select--labeled", label: t('rules.form.applies_to_entity_label'), options: [
                                { value: 'tenant', label: t('rules.scope_badge.tenant') },
                                { value: 'buildings', label: t('rules.form.scope_buildings') },
                                { value: 'building_tag', label: t('rules.form.scope_building_tag') },
                                { value: 'accounts', label: t('rules.form.scope_accounts') },
                                { value: 'account_tag', label: t('rules.form.scope_account_tag') }
                            ], value: form.scopeLevel, onChange: (val) => setForm((prev) => (Object.assign(Object.assign({}, prev), { scopeLevel: val, 
                                // Clear only LOCATION axes (single-axis invariant). Actor
                                // fields (targetTechnicians/targetTeams) are orthogonal (D1)
                                // and must never be cleared by a scope-level change.
                                targetBuildings: [], targetAccounts: [], buildingTag: '', accountTag: '' }))) })),
                    form.scopeLevel === 'buildings' && (React.createElement("div", { className: "tkb-rule-form__field" },
                        React.createElement(ExclusionMultiSelect, { label: t('rules.form.building_label'), placeholder: t('rules.form.building_select_placeholder'), searchPlaceholder: t('rules.form.building_search_placeholder'), emptyMessage: t('rules.form.building_no_results'), asyncSearch: loadBuildingOptions, selected: form.targetBuildings.map((building) => String(building.id)), selectedOptions: form.targetBuildings.map((building) => ({
                                value: String(building.id),
                                label: building.name
                            })), onChange: (values) => setForm((prev) => (Object.assign(Object.assign({}, prev), { targetBuildings: resolveSelectedTargets(values, prev.targetBuildings, buildingOptions) }))) }))),
                    form.scopeLevel === 'accounts' && (React.createElement("div", { className: "tkb-rule-form__field" },
                        React.createElement(ExclusionMultiSelect, { label: t('rules.form.account_label'), placeholder: t('rules.form.account_select_placeholder'), searchPlaceholder: t('rules.form.account_search_placeholder'), emptyMessage: t('rules.form.account_no_results'), asyncSearch: loadAccountOptions, selected: form.targetAccounts.map((account) => String(account.id)), selectedOptions: form.targetAccounts.map((account) => ({
                                value: String(account.id),
                                label: account.name
                            })), onChange: (values) => setForm((prev) => (Object.assign(Object.assign({}, prev), { targetAccounts: resolveSelectedTargets(values, prev.targetAccounts, accountOptions) }))) }))),
                    form.scopeLevel === 'account_tag' && (React.createElement("div", { className: "tkb-rule-form__field" },
                        React.createElement(SelectInput, { id: "tkb-rule-account-tag", className: "qmb-ui-select--labeled", label: t('rules.form.account_tag_label'), options: [{ value: '', label: t('rules.form.select_account_tag') }, ...accountTagSelectOptions], value: form.accountTag, onChange: (val) => setForm((prev) => (Object.assign(Object.assign({}, prev), { accountTag: val }))), showSearch: true, searchPlaceholder: t('rules.form.account_tag_search_placeholder') }))),
                    form.scopeLevel === 'building_tag' && (React.createElement("div", { className: "tkb-rule-form__field" },
                        React.createElement(SelectInput, { id: "tkb-rule-tag", className: "qmb-ui-select--labeled", label: t('rules.form.tag_label'), options: [{ value: '', label: t('rules.form.select_tag') }, ...tagSelectOptions], value: form.buildingTag, onChange: (val) => setForm((prev) => (Object.assign(Object.assign({}, prev), { buildingTag: val }))), showSearch: true, searchPlaceholder: t('rules.form.tag_search_placeholder') }))),
                    React.createElement("div", { className: "tkb-rule-form__field", "data-testid": "actor-targeting-section" },
                        React.createElement(ExclusionMultiSelect, { label: t('rules.form.technician_label'), placeholder: t('rules.form.technician_select_placeholder'), searchPlaceholder: t('rules.form.technician_search_placeholder'), emptyMessage: t('rules.form.technician_no_results'), asyncSearch: loadTechnicianOptions, selected: form.targetTechnicians.map((tech) => String(tech.id)), selectedOptions: form.targetTechnicians.map((tech) => ({
                                value: String(tech.id),
                                label: tech.name
                            })), onChange: (values) => setForm((prev) => {
                                const targetTechnicians = resolveSelectedTargets(values, prev.targetTechnicians, technicianOptions);
                                return Object.assign(Object.assign(Object.assign({}, prev), { targetTechnicians }), actorSurfaceDefault(prev, targetTechnicians, prev.targetTeams, iaEntitled));
                            }) })),
                    React.createElement("div", { className: "tkb-rule-form__field" },
                        React.createElement(ExclusionMultiSelect, { label: t('rules.form.team_label'), placeholder: t('rules.form.team_select_placeholder'), searchPlaceholder: t('rules.form.team_search_placeholder'), emptyMessage: t('rules.form.team_no_results'), asyncSearch: loadTeamOptions, selected: form.targetTeams.map((team) => String(team.id)), selectedOptions: form.targetTeams.map((team) => ({
                                value: String(team.id),
                                label: team.name
                            })), onChange: (values) => setForm((prev) => {
                                const targetTeams = resolveSelectedTargets(values, prev.targetTeams, teamOptions);
                                return Object.assign(Object.assign(Object.assign({}, prev), { targetTeams }), actorSurfaceDefault(prev, prev.targetTechnicians, targetTeams, iaEntitled));
                            }) })),
                    React.createElement("div", { className: "tkb-rule-form__field" },
                        React.createElement("label", { htmlFor: "tkb-rule-directive", className: "tkb-rule-form__label" }, t('rules.form.rule_section_label')),
                        React.createElement("textarea", { id: "tkb-rule-directive", value: form.directive, onChange: (ev) => setForm((prev) => (Object.assign(Object.assign({}, prev), { directive: ev.target.value }))), rows: 4, className: "tkb-rule-form__textarea", placeholder: t('rules.form.rule_placeholder') })),
                    showSurfaceChip && (React.createElement("div", { className: "tkb-rule-form__field", "data-testid": "applies-to-control" },
                        React.createElement("span", { className: "tkb-rule-form__label" }, t('rules.form.assistant_section_label')),
                        React.createElement("div", { className: "tkb-rule-form__assistant" },
                            ipaEntitled && (React.createElement(Checkbox, { id: "tkb-rule-assistant-ipa", checked: form.appliesTo.includes('ipa'), onChange: () => toggleSurface('ipa'), label: t('rules.form.assistant_ipa') })),
                            iaEntitled && (React.createElement(Checkbox, { id: "tkb-rule-assistant-ia", checked: form.appliesTo.includes('ia'), onChange: () => toggleSurface('ia'), label: t('rules.form.assistant_ia') })),
                            form.appliesTo.includes('ia') && iaEntitled && (React.createElement("div", { className: "tkb-rule-form__checks", "data-testid": "relevant-checks-control" },
                                React.createElement(Text, { variant: "small", as: "p", style: { color: 'var(--color-muted, #666)', margin: '0 0 4px' } }, t('rules.form.assistant_ipa_checks_hint')),
                                React.createElement("div", { className: "tkb-rule-form__checks-list" }, designatedCheckOptions.map((opt) => (React.createElement(Checkbox, { key: opt.value, id: `tkb-rule-check-${opt.value}`, checked: form.relevantChecks.includes(opt.value), onChange: () => toggleRelevantCheck(opt.value), label: opt.name }))))))))))),
            React.createElement(BrushawayFooter, null,
                React.createElement(Button, { variant: "secondary", onClick: closeModal, style: { marginRight: '8px' } }, t('rules.form.cancel')),
                React.createElement(Button, { variant: "primary", disabled: isSaving || !canAuthorRules, onClick: handleSubmit }, isSaving ? t('rules.form.saving') : modalState.editingRule ? t('rules.form.save') : t('rules.form.add')))),
        versionsRule && (React.createElement(VersionDrawer, { rule: versionsRule, rulesQueryKey: TKB_QUERY_KEYS.rules, onClose: () => setVersionsRule(null), t: t })),
        React.createElement(Modal, { isOpen: blockConfirmOpen, ariaLabel: t('rules.form.confirm_block_title'), onClose: () => {
                pendingSaveRef.current = undefined;
                setBlockConfirmOpen(false);
            } },
            React.createElement(ModalHeader, { title: t('rules.form.confirm_block_title') }),
            React.createElement(ModalBody, null,
                React.createElement(Text, { as: "p" }, t('rules.form.confirm_block_body', { action: refusedActionLabel() }))),
            React.createElement(ModalFooter, null,
                React.createElement(Button, { variant: "secondary", onClick: () => {
                        pendingSaveRef.current = undefined;
                        setBlockConfirmOpen(false);
                    }, style: { marginRight: '8px' } }, t('rules.form.confirm_block_cancel')),
                React.createElement(Button, { variant: "primary", disabled: isSaving, onClick: () => {
                        const pending = pendingSaveRef.current;
                        pendingSaveRef.current = undefined;
                        submitRule(pending);
                    }, "data-testid": "confirm-hard-block" }, t('rules.form.confirm_block_confirm')))),
        React.createElement(Modal, { isOpen: deleteConfirmRule !== null, ariaLabel: t('rules.delete_modal.title'), onClose: () => {
                setDeleteConfirmRule(null);
                setDeleteError(null);
            } },
            React.createElement(ModalHeader, { title: t('rules.delete_modal.title') }),
            React.createElement(ModalBody, null,
                React.createElement(Text, { as: "p" }, t('rules.delete_modal.body')),
                deleteConfirmRule && (React.createElement("div", { className: "tkb-delete-preview", "data-testid": "rule-delete-preview" },
                    React.createElement("div", { className: "tkb-delete-preview__rule" }, deleteConfirmRule.directive_human),
                    React.createElement("div", { className: "tkb-delete-preview__meta" },
                        React.createElement(AppliesTo, { scopeLevel: deleteConfirmRule.scope_level, targetLabel: scopeTargetLabel(deleteConfirmRule), t: t }),
                        (() => {
                            var _a, _b;
                            const techs = (_a = deleteConfirmRule.target_technicians) !== null && _a !== void 0 ? _a : [];
                            const teams = (_b = deleteConfirmRule.target_teams) !== null && _b !== void 0 ? _b : [];
                            const actorLabel = targetsLabel([...techs, ...teams]);
                            if (!actorLabel)
                                return null;
                            let actorKind = t('rules.applies_to.kind_team');
                            if (techs.length > 0 && teams.length > 0) {
                                actorKind = t('rules.applies_to.kind_actor_mixed');
                            }
                            else if (techs.length > 0) {
                                actorKind = t('rules.applies_to.kind_technician');
                            }
                            return (React.createElement("span", { className: "tkb-applies tkb-applies--actor" },
                                React.createElement("span", { className: "tkb-applies__target" }, actorLabel),
                                React.createElement("span", { className: "tkb-applies__meta" },
                                    React.createElement("i", { className: "fa-light fa-user", "aria-hidden": "true" }),
                                    actorKind)));
                        })(),
                        showSurfaceChip && React.createElement(AssistantPill, { surfaces: deleteConfirmRule.applies_to, t: t }),
                        React.createElement("span", { className: "tkb-source__version" }, t('rules.version_label', { version: deleteConfirmRule.version }))))),
                deleteError && (React.createElement(Text, { as: "p", style: { color: 'var(--color-danger, #c0392b)', marginTop: '8px' } }, deleteError))),
            React.createElement(ModalFooter, null,
                React.createElement(Button, { variant: "secondary", onClick: () => {
                        setDeleteConfirmRule(null);
                        setDeleteError(null);
                    }, style: { marginRight: '8px' } }, t('rules.delete_modal.cancel')),
                React.createElement(Button, { variant: "danger", disabled: deleteMutation.isLoading, onClick: () => {
                        if (deleteConfirmRule !== null)
                            deleteMutation.mutate(deleteConfirmRule.id);
                    } }, deleteMutation.isLoading ? t('rules.delete_modal.deleting') : t('rules.delete_modal.confirm'))))));
}
// ── Main component ─────────────────────────────────────────────────────────────
const DEFAULT_CAPABILITIES = {
    ai_rules_knowledge: false,
    ai_authoring_chat: false,
    ia_present: false,
    ipa_present: true
};
function TenantKnowledgeBaseSettings({ aiChatWidgetEnabled, scopeLabels, prohibitableScopeLabels = {}, capabilities = DEFAULT_CAPABILITIES, buildingTags = [], accountTags = [], issueTypes = [], designatedRuleContextChecks = [] }) {
    if (!aiChatWidgetEnabled) {
        return (React.createElement("div", { className: "workspace__body" },
            React.createElement("div", { className: "workspace__major--listing tkb-workspace" },
                React.createElement(WorkspaceHeader, { title: t('title'), divider: false }),
                React.createElement("div", { className: "no-data-message", style: { padding: '24px' } }, t('not_enabled')))));
    }
    const iaPresent = capabilities.ia_present;
    const tabs = [
        { id: 'docs', label: t('tabs.documents'), content: React.createElement(DocsTab, null) },
        {
            id: 'rules',
            label: t('tabs.rules'),
            content: (React.createElement(RulesTab, { scopeLabels: scopeLabels, prohibitableScopeLabels: prohibitableScopeLabels, capabilities: capabilities, buildingTags: buildingTags, accountTags: accountTags, issueTypes: issueTypes, designatedRuleContextChecks: designatedRuleContextChecks }))
        }
    ];
    if (iaPresent) {
        tabs.push({
            id: 'preview',
            label: t('tabs.preview'),
            content: React.createElement(PreviewPanel, { issueTypes: issueTypes, t: t })
        });
    }
    return (React.createElement("div", { className: "workspace__body" },
        React.createElement("div", { className: "workspace__major--listing tkb-workspace" },
            React.createElement(WorkspaceHeader, { title: t('title'), divider: false }),
            React.createElement("p", { className: "tkb-intro" }, t('intro')),
            React.createElement(TabView, { defaultTabId: "docs", tabs: tabs }))));
}
// ── Entry point (wraps in QueryClientProvider) ────────────────────────────────
function TenantKnowledgeBase(props) {
    const queryClient = useMemo(() => new QueryClient(), []);
    return (React.createElement(QueryClientProvider, { client: queryClient },
        React.createElement(TenantKnowledgeBaseSettings, Object.assign({}, props))));
}
export default TenantKnowledgeBase;
